import React, { useEffect, useState } from 'react';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { signInWithPassword, getErrorMessage } from '../api/authUtils';
import {
  identityAuthErrorMessage,
  signInWithWorkspaceEmail,
  startWorkspaceGoogleSignIn,
} from '../centralIdentityAuth';
import { usesCentralIdentity } from '../sessionAccess';
import { expandSignInName, managedSignInDomain } from '../managedSignIn';
import { listLocalDevUsers, startLocalDevSession } from '../localDevSession';
import { useCurrentUserRole } from '../../../shared/session/useCurrentUserRole';
import { getDefaultRouteForRole } from '../utils/roleRouting';
import { APP_SHELL_ROUTE_PREFIX } from '../../../app/router/routeManifest';
import { getLandingUrl } from '../../../shared/config/landingUrl';

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.3h6.46c-.28 1.5-1.12 2.77-2.39 3.62v3h3.86c2.26-2.08 3.56-5.14 3.56-8.65z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.86-3c-1.07.72-2.45 1.15-4.09 1.15-3.14 0-5.8-2.12-6.75-4.97H1.27v3.12C3.25 21.3 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.25 14.27c-.24-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.61H1.27C.46 8.24 0 10.06 0 12s.46 3.76 1.27 5.39l3.98-3.12z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.45-3.45C17.95 1.09 15.24 0 12 0 7.31 0 3.25 2.7 1.27 6.61l3.98 3.12C6.2 6.88 8.86 4.75 12 4.75z"
      />
    </svg>
  );
}

/** Development builds only: sign in as a local profile without Identity Platform. */
function LocalDevSignIn({ onSignedIn }) {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    listLocalDevUsers().then((rows) => { if (active) setUsers(rows); });
    return () => { active = false; };
  }, []);
  if (!users.length) return null;
  const signIn = async (user) => {
    setBusy(user.user_profile_id);
    setError('');
    try {
      await startLocalDevSession(user.user_profile_id);
      onSignedIn();
    } catch (err) {
      setError(err?.message || 'Local sign-in failed.');
      setBusy(null);
    }
  };
  return (
    <div className="mt-6 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid="local-dev-sign-in">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-300">Local development</p>
      <p className="mt-1 text-xs text-gray-400">Sign in as a local profile. Available only on this machine.</p>
      <div className="mt-3 space-y-2">
        {users.map((user) => (
          <button
            key={user.user_profile_id}
            type="button"
            disabled={Boolean(busy)}
            onClick={() => signIn(user)}
            className="flex w-full items-center justify-between rounded-lg border border-slate-700 px-3 py-2 text-left text-sm text-gray-100 hover:bg-slate-800 disabled:opacity-60"
          >
            <span className="truncate">{user.name || user.email}</span>
            <span className="ml-3 shrink-0 text-xs text-gray-400">{busy === user.user_profile_id ? 'Signing in…' : user.role}</span>
          </button>
        ))}
      </div>
      {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: userRole, isLoading: isRoleLoading } = useCurrentUserRole();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const contactSalesUrl = getLandingUrl('/contact');
  const centralIdentity = usesCentralIdentity();
  // A preconfigured account's short username needs the workspace's managed domain.
  const acceptsUsername = Boolean(managedSignInDomain());
  const requestedReturnTo = location.state?.returnTo;
  const safeReturnTo =
    typeof requestedReturnTo === 'string' &&
    requestedReturnTo.startsWith('/') &&
    !requestedReturnTo.startsWith('//')
      ? requestedReturnTo
      : null;

  useEffect(() => {
    if (location.state?.message || location.state?.error) {
      setInfoMessage(location.state.message || '');
      setError(location.state.error || '');
      if (location.state?.email) {
        setFormData((prev) => ({ ...prev, email: location.state.email }));
      }
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [location.state, location.pathname, location.search, navigate]);

  if (isAuthenticated && !isRoleLoading && !isSubmitting) {
    return <Navigate to={safeReturnTo || (userRole ? getDefaultRouteForRole(userRole) : APP_SHELL_ROUTE_PREFIX)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setInfoMessage('');

    try {
      const { session, role } = await signInWithPassword(formData.email, formData.password);
      if (session) {
        const redirectTo = safeReturnTo || (role ? getDefaultRouteForRole(role) : APP_SHELL_ROUTE_PREFIX);
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    setError('');
    setInfoMessage('');
    try {
      await startWorkspaceGoogleSignIn();
    } catch (err) {
      setError(identityAuthErrorMessage(err));
      setIsSubmitting(false);
    }
  };

  const handleWorkspaceEmailSignIn = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    setInfoMessage('');
    try {
      const session = await signInWithWorkspaceEmail(expandSignInName(formData.email), formData.password);
      if (session) {
        const redirectTo = safeReturnTo || (userRole ? getDefaultRouteForRole(userRole) : APP_SHELL_ROUTE_PREFIX);
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError(identityAuthErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 rounded-2xl blur-3xl" />
        <div className="absolute inset-0 bg-slate-900/80 rounded-2xl backdrop-blur-sm" />

        <div className="relative bg-slate-900/50 p-6 sm:p-8 rounded-2xl border border-slate-800 shadow-2xl">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
              Welcome Back
            </h2>
            {centralIdentity ? null : (
              <p className="mt-2 text-sm sm:text-base text-gray-400">Sign in to your account to continue</p>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">{error}</div>
          )}

          {infoMessage && (
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm">
              {infoMessage}
            </div>
          )}

          {centralIdentity ? (
            <div className="space-y-4 sm:space-y-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isSubmitting}
                className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-700 bg-white px-4 py-3.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100 disabled:cursor-wait disabled:opacity-60"
              >
                <GoogleMark />
                {isSubmitting ? 'Opening Google…' : 'Continue with Google'}
              </button>
              <form onSubmit={handleWorkspaceEmailSignIn} className="space-y-4">
                <div className="space-y-1">
                  <label htmlFor="workspace-email" className="text-sm font-medium text-gray-300">
                    {acceptsUsername ? 'Email or username' : 'Email'}
                  </label>
                  <input
                    id="workspace-email"
                    name="email"
                    type={acceptsUsername ? 'text' : 'email'}
                    required
                    autoComplete="username"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="block w-full px-3 py-3 sm:py-3.5 bg-slate-800/50 border border-slate-700 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-gray-500 text-gray-100 text-sm sm:text-base disabled:opacity-60"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="workspace-password" className="text-sm font-medium text-gray-300">
                    Password
                  </label>
                  <input
                    id="workspace-password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="block w-full px-3 py-3 sm:py-3.5 bg-slate-800/50 border border-slate-700 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-gray-500 text-gray-100 text-sm sm:text-base disabled:opacity-60"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 sm:py-3.5 px-4 rounded-lg border border-slate-600 text-sm sm:text-base font-semibold text-white hover:bg-slate-800 disabled:cursor-wait disabled:opacity-60"
                >
                  {isSubmitting ? 'Please wait...' : 'Sign in'}
                </button>
                <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm text-cyan-400 hover:text-cyan-300">
                  Forgot your password?
                </button>
              </form>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm font-medium text-gray-300">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-9 sm:pl-10 pr-3 py-3 sm:py-3.5 bg-slate-800/50 border border-slate-700 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-gray-500 text-gray-100 text-sm sm:text-base"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="text-sm font-medium text-gray-300">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-3 sm:py-3.5 bg-slate-800/50 border border-slate-700 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent placeholder-gray-500 text-gray-100 text-sm sm:text-base"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-700 bg-slate-800/50 text-cyan-400 focus:ring-cyan-400 focus:ring-offset-0"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-400">
                  Remember me
                </label>
              </div>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-cyan-400 hover:text-cyan-300 touch-manipulation self-start sm:self-auto"
              >
                Forgot your password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 sm:py-3.5 px-4 rounded-lg relative overflow-hidden group touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-400 transition-transform duration-300 group-hover:scale-105 group-disabled:scale-100" />
              <span className="relative text-white font-semibold group-hover:scale-105 transition-transform duration-300 inline-block text-sm sm:text-base">
                {isSubmitting ? 'Please wait...' : 'Sign in'}
              </span>
            </button>
          </form>
          )}

          {import.meta.env.DEV ? (
            <LocalDevSignIn onSignedIn={() => navigate(safeReturnTo || APP_SHELL_ROUTE_PREFIX, { replace: true })} />
          ) : null}

          {centralIdentity ? null : (
          <div className="mt-6 text-center text-sm text-gray-400">
            Don&apos;t have an account?{' '}
            {contactSalesUrl ? (
              <a
                href={contactSalesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 touch-manipulation"
              >
                Contact us to get started
              </a>
            ) : (
              <span className="text-gray-500">Contact your organization for access.</span>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
